import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { LoadError, errMessage } from "@/components/LoadError";
import { PerWeek, ZonderOmPanel } from "@/components/overzicht/PerWeek";
import LocatieDossier from "@/components/overzicht/LocatieDossier";
import { Metric, SectieBalk, AandachtTegel, KleineTegel, StandTabel, PerOmKaart, TerugkerendSectie } from "@/components/overzicht/NuParts";
import { weekNr } from "@/components/overzicht/overzichtUtils";

const seg = (on) => `px-3 py-1.5 rounded-sm text-sm font-semibold transition-colors ${on ? "bg-zinc-950 text-white" : "text-zinc-500 hover:bg-zinc-100"}`;

function Segment({ opties, value, onChange, testid }) {
  return (
    <div data-testid={testid} className="flex gap-0.5 rounded-md border border-zinc-300 bg-white p-0.5">
      {opties.map(([k, l]) => <button key={k} data-testid={`${testid}-${k}`} onClick={() => onChange(k)} className={seg(value === k)}>{l}</button>)}
    </div>
  );
}

const KleineGroep = ({ rows, onOpen, leeg }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 mb-3 md:mb-4">
    {rows.length === 0 ? <p className="text-sm text-zinc-500">{leeg}</p> : rows.map((r) => <KleineTegel key={r.locatie} r={r} onOpen={onOpen} />)}
  </div>
);

function NuMetrics({ nu }) {
  const m = nu.metrics;
  const hc = nu.huidigeCall;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2 md:gap-3 mb-3 md:mb-4" data-testid="nu-metrics">
      <Metric testid="metric-calls" label={hc ? `Gebeld ${hc.callLabel} · over week ${hc.overWeekNr}` : "Gebeld"} value={`${m.callsDezeWeek}/${m.callsTotaal}`} />
      <Metric testid="metric-cijfers" label="Klaar" value={`${m.cijfersCompleet ?? 0}/${m.callsDezeWeek}`} />
      <Metric testid="metric-rood" label="Rood" value={m.rood ?? m.roodBijLaatsteCall} rood={(m.rood ?? m.roodBijLaatsteCall) > 0} />
      <Metric testid="metric-rzo" label="Rood zonder afspraak" value={m.roodZonderAfspraak ?? m.roodZonderOpvolging} rood={(m.roodZonderAfspraak ?? m.roodZonderOpvolging) > 0} />
      <Metric testid="metric-telaat" label="Afspraken te laat" value={m.teLaat} rood={m.teLaat > 0} />
      <Metric testid="metric-open" label="Open afspraken" value={m.openAfspraken} />
      <Metric testid="metric-voorbereiding" label={`Deze week: voorbereiding voor ${nu.eerstvolgendeCall.callLabel || `wk ${weekNr(m.voorbereidingWeek)}`} begonnen`} value={`${m.voorbereidingEerstvolgende}/${m.voorbereidingTotaal}`} />
    </div>
  );
}

function NuView({ nu, week, weergave, isOversight, onOpen, reloadWeek }) {
  const per = (s) => nu.locaties.filter((r) => r.status === s);
  const aandacht = per("aandacht");
  const bellen = per("nog-te-bellen");
  const nieuw = per("geen-call");
  const koers = per("op-koers");
  return (
    <>
      <NuMetrics nu={nu} />
      {weergave === "tabel" ? <div className="mb-3 md:mb-4"><StandTabel rows={nu.locaties} onOpen={onOpen} /></div> : (
        <>
          <SectieBalk testid="sectie-aandacht" titel="Aandacht nodig" aantal={aandacht.length} hint="rood, te laat, cijfers ontbreken of geen call deze week" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-3 md:mb-4">
            {aandacht.length === 0 ? <p className="text-sm text-zinc-500">Niets dat aandacht vraagt.</p> : aandacht.map((r) => <AandachtTegel key={r.locatie} r={r} onOpen={onOpen} />)}
          </div>
          {nu.isMaandag && (
            <>
              <SectieBalk testid="sectie-nog-te-bellen" titel="Nog te bellen vandaag" aantal={bellen.length} hint={`alle locaties: ${nu.metrics.compleetDezeWeek ?? 0} van ${nu.metrics.callsTotaal} klaar · rode, te late en nieuwe locaties staan in hun eigen groep`} />
              <KleineGroep rows={bellen} onOpen={onOpen} leeg="Alles is gebeld." />
            </>
          )}
          {nieuw.length > 0 && (
            <>
              <SectieBalk testid="sectie-geen-call" titel="Nog geen call" aantal={nieuw.length} hint="nieuwe locatie" />
              <KleineGroep rows={nieuw} onOpen={onOpen} />
            </>
          )}
          <SectieBalk testid="sectie-op-koers" titel="Op koers" aantal={koers.length} hint="omzet, PK en bijverkoop behaald, WOW niet op Nee, niets te laat" />
          <KleineGroep rows={koers} onOpen={onOpen} leeg="Nog geen locatie op koers." />
        </>
      )}
      <TerugkerendSectie rows={nu.locaties} onOpen={onOpen} vanafLabel={nu.klaarVanafLabel} />
      {week && isOversight && week.locationOmsVersion !== undefined && <ZonderOmPanel data={week} reload={reloadWeek} compact />}
      {week && (
        <>
          <SectieBalk testid="sectie-per-om" titel="Per OM" hint={nu.huidigeCall ? `call ${nu.huidigeCall.callLabel} · over week ${nu.huidigeCall.overWeekNr}` : ""} />
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">{week.perOm.map((o) => <PerOmKaart key={o.om} o={o} />)}</div>
        </>
      )}
    </>
  );
}

export default function Overzicht() {
  const { user } = useAuth();
  const isOversight = user.role === "oversight";
  const [modus, setModus] = useState("nu");
  const [weergave, setWeergave] = useState("tegels");
  const [nu, setNu] = useState(null);
  const [week, setWeek] = useState(null);
  const [err, setErr] = useState("");
  const [dossier, setDossier] = useState(null);

  const loadNu = () => api.get("/overzicht/nu").then((r) => {
    setNu(r.data); setErr("");
    return api.get(`/overzicht?week=${r.data.huidigeWeek}&weekritme_week=${r.data.kalenderWeek}`).then((w) => setWeek(w.data));
  }).catch((e) => setErr(errMessage(e)));
  const reloadWeek = () => nu && api.get(`/overzicht?week=${nu.huidigeWeek}&weekritme_week=${nu.kalenderWeek}`).then((w) => setWeek(w.data)).catch((e) => setErr(errMessage(e)));
  useEffect(() => { loadNu(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const { hash } = useLocation();
  // v14 F6 + R1: /overzicht#terugkerend scrolt één keer naar Terugkerend rood, onder de sticky kop (als R17).
  const gescrold = useRef(false);
  useEffect(() => {
    if (gescrold.current || !nu || !week || hash !== "#terugkerend") return;   // pas als alles eronder er ook staat
    const el = document.getElementById("terugkerend");
    if (!el) return;
    el.style.scrollMarginTop = `${(document.querySelector('[data-testid="sticky-kop"]')?.offsetHeight || 0) + 12}px`;
    el.scrollIntoView({ block: "start" });
    gescrold.current = true;
  }, [nu, week, hash]);

  return (
    <div data-testid="overzicht-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3 md:mb-4">
        <div>
          <h1 className="font-heading text-2xl font-black tracking-tight text-zinc-900">Overzicht</h1>
          <p className="text-sm text-zinc-500" data-testid="overzicht-subtitel">
            Stand van alle locaties, over alle weken.{nu && ` Eerstvolgende call: ${nu.eerstvolgendeCall.label}.`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {modus === "nu" && <Segment testid="overzicht-weergave" value={weergave} onChange={setWeergave} opties={[["tegels", "Tegels"], ["tabel", "Tabel"]]} />}
          <Segment testid="overzicht-modus" value={modus} onChange={setModus} opties={[["nu", "Nu"], ["week", "Per week"]]} />
        </div>
      </div>
      {modus === "week" ? <PerWeek /> : (
        <>
          <LoadError error={err} onRetry={loadNu} testid="overzicht-load-error" className="mb-4" />
          {err ? null : !nu ? <div data-testid="overzicht-loading" className="bg-white rounded-[10px] border border-zinc-200 p-12 text-center text-sm text-zinc-400">Laden…</div>
            : <NuView nu={nu} week={week} weergave={weergave} isOversight={isOversight} onOpen={setDossier} reloadWeek={reloadWeek} />}
        </>
      )}
      <LocatieDossier locatie={dossier} onClose={() => setDossier(null)} onChanged={loadNu} />
    </div>
  );
}
